import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Space, Modal, message, Select, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdvWithClient, AdvRequest, AdvClient } from '../../types/adv';
import { advService } from '../../services/advService';
import { advClientService } from '../../services/advClientService';

interface AdvFormProps {
  adv?: AdvWithClient;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdvForm: React.FC<AdvFormProps> = ({
  adv,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<AdvClient[]>([]);
  const [uploading, setUploading] = useState(false);

  const isEditing = !!adv;
  const title = isEditing 
    ? t('advertising.advs.form.edit_title') 
    : t('advertising.advs.form.create_title');

  // Fetch clients for the select dropdown
  const fetchClients = async () => {
    try {
      const data = await advClientService.getAllAdvClients();
      setClients(data.filter(client => client.client_active));
    } catch (error) {
      console.error('Error fetching clients:', error);
      message.error(t('advertising.advs.notifications.clients_error'));
    }
  };

  useEffect(() => {
    if (isVisible) {
      fetchClients();
    }
  }, [isVisible]);

  const handleSubmit = async (values: AdvRequest) => {
    setIsLoading(true);
    try {
      if (isEditing && adv) {
        await advService.updateAdv(adv.id, values);
        message.success(t('advertising.advs.notifications.update_success'));
      } else {
        await advService.createAdv(values);
        message.success(t('advertising.advs.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting advertisement form:', error);
      message.error(t('advertising.advs.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (file: File, fieldName: string) => {
    setUploading(true);
    try {
      const filePath = await advService.uploadAdvFile(file);
      form.setFieldsValue({ [fieldName]: filePath });
      message.success(t('advertising.advs.notifications.upload_success'));
      return false; // Prevent default upload behavior
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error(t('advertising.advs.notifications.upload_error'));
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Reset form when modal opens or adv changes
  React.useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        adv_name: adv?.adv_name || '',
        adv_file: adv?.adv_file || '',
        adv_file_2000: adv?.adv_file_2000 || '',
        adv_file_1000: adv?.adv_file_1000 || '',
        adv_file_500: adv?.adv_file_500 || '',
        adv_client_id: adv?.adv_client_id || undefined,
      });
    }
  }, [form, adv, isVisible]);

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          adv_name: adv?.adv_name || '',
          adv_file: adv?.adv_file || '',
          adv_file_2000: adv?.adv_file_2000 || '',
          adv_file_1000: adv?.adv_file_1000 || '',
          adv_file_500: adv?.adv_file_500 || '',
          adv_client_id: adv?.adv_client_id || undefined,
        }}
      >
        <Form.Item
          name="adv_name"
          label={t('advertising.advs.form.name')}
          rules={[
            {
              required: true,
              message: t('advertising.advs.form.name_required'),
            },
          ]}
        >
          <Input placeholder={t('advertising.advs.form.name_placeholder')} />
        </Form.Item>

        <Form.Item
          name="adv_client_id"
          label={t('advertising.advs.form.client')}
          rules={[
            {
              required: true,
              message: t('advertising.advs.form.client_required'),
            },
          ]}
        >
          <Select
            placeholder={t('advertising.advs.form.client_placeholder')}
            options={clients.map(client => ({
              label: client.client_name,
              value: client.id,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="adv_file"
          label={t('advertising.advs.form.file')}
          rules={[
            {
              required: true,
              message: t('advertising.advs.form.file_required'),
            },
          ]}
        >
          <Input.Group compact>
            <Input
              value={form.getFieldValue('adv_file')}
              onChange={(e) => form.setFieldsValue({ adv_file: e.target.value })}
              placeholder={t('advertising.advs.form.file_placeholder')}
              style={{ width: 'calc(100% - 120px)' }}
            />
            <Upload
              beforeUpload={(file) => handleFileUpload(file, 'adv_file')}
              showUploadList={false}
              accept="video/*,audio/*,image/*"
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={uploading}
                style={{ width: '120px' }}
              >
                {t('advertising.advs.form.upload')}
              </Button>
            </Upload>
          </Input.Group>
        </Form.Item>

        <Form.Item
          name="adv_file_2000"
          label={t('advertising.advs.form.file_2000')}
          rules={[
            {
              required: true,
              message: t('advertising.advs.form.file_2000_required'),
            },
          ]}
        >
          <Input.Group compact>
            <Input
              value={form.getFieldValue('adv_file_2000')}
              onChange={(e) => form.setFieldsValue({ adv_file_2000: e.target.value })}
              placeholder={t('advertising.advs.form.file_2000_placeholder')}
              style={{ width: 'calc(100% - 120px)' }}
            />
            <Upload
              beforeUpload={(file) => handleFileUpload(file, 'adv_file_2000')}
              showUploadList={false}
              accept="video/*,audio/*,image/*"
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={uploading}
                style={{ width: '120px' }}
              >
                {t('advertising.advs.form.upload')}
              </Button>
            </Upload>
          </Input.Group>
        </Form.Item>

        <Form.Item
          name="adv_file_1000"
          label={t('advertising.advs.form.file_1000')}
          rules={[
            {
              required: true,
              message: t('advertising.advs.form.file_1000_required'),
            },
          ]}
        >
          <Input.Group compact>
            <Input
              value={form.getFieldValue('adv_file_1000')}
              onChange={(e) => form.setFieldsValue({ adv_file_1000: e.target.value })}
              placeholder={t('advertising.advs.form.file_1000_placeholder')}
              style={{ width: 'calc(100% - 120px)' }}
            />
            <Upload
              beforeUpload={(file) => handleFileUpload(file, 'adv_file_1000')}
              showUploadList={false}
              accept="video/*,audio/*,image/*"
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={uploading}
                style={{ width: '120px' }}
              >
                {t('advertising.advs.form.upload')}
              </Button>
            </Upload>
          </Input.Group>
        </Form.Item>

        <Form.Item
          name="adv_file_500"
          label={t('advertising.advs.form.file_500')}
          rules={[
            {
              required: true,
              message: t('advertising.advs.form.file_500_required'),
            },
          ]}
        >
          <Input.Group compact>
            <Input
              value={form.getFieldValue('adv_file_500')}
              onChange={(e) => form.setFieldsValue({ adv_file_500: e.target.value })}
              placeholder={t('advertising.advs.form.file_500_placeholder')}
              style={{ width: 'calc(100% - 120px)' }}
            />
            <Upload
              beforeUpload={(file) => handleFileUpload(file, 'adv_file_500')}
              showUploadList={false}
              accept="video/*,audio/*,image/*"
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={uploading}
                style={{ width: '120px' }}
              >
                {t('advertising.advs.form.upload')}
              </Button>
            </Upload>
          </Input.Group>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t('advertising.advs.form.submit')}
            </Button>
            <Button onClick={onClose}>
              {t('advertising.advs.form.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdvForm; 